"use client";

import { signOut } from "next-auth/react";
import { useMemo, useState } from "react";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconSettings from "@/app/(components)/generics/Icons/IconSettings";
import IconSignOut from "@/app/(components)/generics/Icons/IconSignOut";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { Tooltip, TooltipPosition } from "@/app/(components)/generics/Tooltip/Tooltip";
import UserPicture from "@/app/(components)/generics/UserPicture/UserPicture";
import { ModalStore } from "@/app/stores/ModalStore";
import { SidebarStore } from "@/app/stores/SidebarStore";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PublicUser } from "@/app/types/PublicUser";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./SidebarAccount.module.css";

type Props = {
  user: PublicUser;
  lang: string;
  isCollapsed: boolean;
};

export default function SidebarAccount(props: Props) {
  const { user, lang, isCollapsed } = props;

  const [isOpen, setIsOpen] = useState(false);

  const closeSidebarOnMobile = SidebarStore((state) => state.closeSidebarOnMobile);

  const displayLetter = user.firstName.at(0)! + user.lastName.at(0)!;
  const fullName = `${user.firstName} ${user.lastName}`;

  const handleOpenSettings = () => {
    setIsOpen(false);
    closeSidebarOnMobile();
    ModalStore.getState().openSettings(lang);
  };

  const handleSignOut = () => {
    closeSidebarOnMobile();
    signOut();
  };

  const popoverPosition = useMemo(
    () =>
      isCollapsed
        ? { default: { bottom: "0", left: "calc(100% + 12px)" } }
        : { default: { bottom: "calc(100% + 8px)", left: "0" } },
    [isCollapsed],
  );

  return (
    <Popover
      isVisible={isOpen}
      setIsVisible={setIsOpen}
      openAfterClick={true}
      closeWhenClickOutside={true}
      className={styles.popover}
    >
      <PopoverTrigger
        className={UStyles.classNames(
          styles.trigger,
          isCollapsed ? styles.triggerCollapsed : "",
          !isCollapsed && isOpen ? styles.triggerActive : "",
        )}
      >
        {isCollapsed ? (
          <Tooltip content={fullName} position={TooltipPosition.RIGHT}>
            <UserPicture className={styles.avatar} displayLetter={displayLetter} />
          </Tooltip>
        ) : (
          <div className={styles.triggerContent}>
            <div className={styles.triggerLeft}>
              <UserPicture className={styles.avatar} displayLetter={displayLetter} />

              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {user.practitionerProfile && <>{T(TKey.DR, lang)} </>}
                  {fullName}
                </span>
                <span className={styles.userMeta}>
                  {user.practitionerProfile?.profession || user.email}
                </span>
              </div>
            </div>

            <div className={styles.chevron}>
              {isOpen ? <IconArrowUp size={20} /> : <IconArrowDown size={20} />}
            </div>
          </div>
        )}
      </PopoverTrigger>

      <PopoverContent className={styles.content} position={popoverPosition}>
        <div className={styles.accountData}>
          <h3>
            {user.practitionerProfile && <>{T(TKey.DR, lang)} </>}
            {fullName}
          </h3>
          <p>{user.email}</p>
        </div>

        <div className={styles.separator} />

        <button className={styles.element} onClick={handleOpenSettings}>
          <IconSettings />
          <span>{T(TKey.SETTINGS, lang)}</span>
        </button>

        <button className={styles.element} onClick={handleSignOut}>
          <IconSignOut />
          <span>{T(TKey.SIGN_OUT, lang)}</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
